<script>
  import { getContext, onMount, tick } from "svelte";
  import { useNavigate } from "svelte-navigator";
  import { useUrlParams } from "@stores/urlStore.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { Accordion, SectionWrapper, Button } from "paxray-components-svelte";
  import Header from "@components/Header.svelte";
  import SideKPI from "@components/sideKPI.svelte";
  import ProgressIndicator from "@components/ProgressIndicator.svelte";
  import ProgressStep from "@components/ProgressStep.svelte";
  import CardColumn from "@components/CardColumn.svelte";
  import { ROUTES } from "@utils/routes.js";
  import CheckIcon from "@components/layout/sideNav/icons/CheckIcon.svelte";
  import CheckEmptyIcon from "@components/layout/sideNav/icons/CheckEmptyIcon.svelte";
  import Elipse from "lib/icons/Elipse.svelte";
  import CheckXIcon from "@components/layout/sideNav/icons/CheckXIcon.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";
  import { Loading } from "carbon-components-svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";

  const role = getContext("role");

  let projectInfo = [
    // TODO(PROJECT_PROGRESS_REAL_METRICS): Re-enable if project start timing
    // should be shown as a right-side metric again.
    // {
    //   name: "Days from Start",
    //   value: "-",
    //   id: "days_from_start",
    // },
    {
      name: "Users",
      value: "-",
      id: "total_users",
    },
    {
      name: "Teams",
      value: "-",
      id: "total_teams",
    },
    {
      name: "Registrations",
      value: "-",
      id: "total_registrations",
      tooltipText: "Total number of task registrations.",
    },
    {
      name: "Last Registration",
      value: "-",
      id: "last_registration",
      tooltipText: "Date of the most recent task registration.",
    },
    {
      name: "Setup Completed",
      value: "-",
      id: "setup_completed",
      tooltipText: "Date the last required Setup step was completed.",
    },
    {
      name: "Scoping Completed",
      value: "-",
      id: "scoping_completed",
      tooltipText: "Date the last required Scoping step was completed.",
    },
    {
      name: "Discovery Completed",
      value: "-",
      id: "discovery_completed",
      tooltipText:
        "Date task registration and all enabled review tasks were completed.",
    },
    // TODO(PROJECT_PROGRESS_REAL_METRICS): Re-enable once the backend exposes
    // a project-specific last data-push / DB-entry timestamp.
    // {
    //   name: "Last Data Push Date",
    //   value: "09.04.2026",
    //   id: "last_data_push_date",
    //   tooltipText: "Date when data was last successfully sent to the server",
    // },
    // TODO(PROJECT_PROGRESS_REAL_METRICS): Re-enable once interaction volume
    // is available as a reliable project-specific metric.
    // {
    //   name: "AVG Interactions per Day",
    //   value: "543k",
    //   id: "avg_interactions_per_day",
    //   tooltipText: "Average number of user interactions per day.",
    // },
    // {
    //   name: "Interactions last 24 hours",
    //   value: "123k",
    //   id: "total_interactions_last_24_hours",
    //   tooltipText: "Number of user interactions in the last 24 hours",
    // },
  ];

  // Data Health overview is loaded automatically with a cheap backend smoke
  // check. These KPIs only answer whether required raw recording streams exist
  // for the currently selected data-bearing phase/configuration.
  let dataHealthInfo = [
    {
      name: "Applications",
      value: null,
      id: "applications_status",
      tooltipText:
        "Checks whether application activity exists in the project recordings. App recordings may also contain window titles to distinguish screens within the same application.",
      statusText:
        "Not checked yet. This overview check verifies whether any application recording data is available.",
    },
    {
      name: "Mouseclicks",
      value: null,
      id: "mouseclicks_status",
      tooltipText:
        "Checks whether mouse click activity exists in the project recordings.",
      statusText:
        "Not checked yet. This overview check verifies whether any mouse click recording data is available.",
    },
    {
      name: "Keystrokes",
      value: null,
      id: "keystrokes_status",
      tooltipText:
        "Checks whether keyboard activity exists in the project recordings.",
      statusText:
        "Not checked yet. This overview check verifies whether any keyboard recording data is available.",
    },
    {
      name: "UI Elements",
      value: null,
      id: "ui_elements_status",
      tooltipText:
        "Checks whether UI element recording data exists for the current phase/configuration.",
      statusText:
        "Not checked yet. This overview check verifies whether UI element recording data is available.",
    },
    // TODO(PROJECT_PROGRESS_REAL_METRICS): Re-enable once interaction-type
    // health (copy, paste, right-click, shortcuts, etc.) is available.
    // {
    //   name: "Interaction",
    //   value: true,
    //   id: "interaction_status",
    //   tooltipText:
    //     "Shows whether the configured interaction types are being captured successfully.",
    // },
  ];

  // Admin-only detail checks are loaded manually via the button below. They may
  // read raw application timestamps for recency, so they stay opt-in and are not
  // run as part of the normal page-load path.
  let dataHealthDetailsInfo = [
    {
      name: "User Coverage",
      status: null,
      id: "recorder_identities_sending_data",
      tooltipText:
        "Checks whether the latest phase/configuration has enough clients configured for the assigned users.",
      statusText:
        "Not checked yet. This setup check compares configured clients with assigned users.",
    },
    {
      name: "Current User Activity",
      status: null,
      id: "current_user_activity",
      tooltipText:
        "Checks whether clients for the latest phase/configuration have sent recent recording data.",
      statusText:
        "Not checked yet. This check uses the latest event per client and compares it with the assigned-user target.",
    },
    {
      name: "Team Data Coverage",
      status: null,
      id: "teams_sending_data",
      tooltipText:
        "Checks whether each project team with assigned users has at least one client that sent recording data in the latest phase/configuration.",
      statusText:
        "Not checked yet. This check verifies whether each assigned team has any received recording data.",
    },
    {
      name: "Latest Data Received",
      status: null,
      id: "last_data_received",
      tooltipText:
        "Checks how recent the newest received recording event is across the expected streams.",
      statusText:
        "Not checked yet. This check uses the newest received event timestamp in the latest phase/configuration.",
    },
    {
      name: "Event Streams",
      status: null,
      id: "empty_event_streams",
      tooltipText:
        "Checks whether application, mouse, keyboard, and UI element recording streams exist for the latest phase/configuration.",
      statusText:
        "Not checked yet. This check only verifies whether each expected stream has received any data.",
    },
  ];

  const dataHealthStatusRules = {
    applications_status: "Green = application data found, red = missing.",
    mouseclicks_status: "Green = mouse click data found, red = missing.",
    keystrokes_status: "Green = keyboard data found, red = missing.",
    ui_elements_status: "Green = UI element data found, red = missing.",
    recorder_identities_sending_data:
      "Green = coverage complete, yellow = partial, red = none.",
    current_user_activity:
      "Green = target coverage <= 2 days, yellow = some <= 3 days, red = none/currently older.",
    teams_sending_data:
      "Green = all teams sent data, yellow = some, red = none.",
    last_data_received: "Green <= 2 days, yellow <= 3 days, red older/missing.",
    empty_event_streams:
      "Green = all streams present, yellow = one missing, red = two or more missing.",
  };

  const MIN_WHITELIST_DURATION_DAYS = 7;

  let setupSteps = [
    {
      id: "CONFIGURE",
      name: "Define Project Scope",
      description: "Choose the processes and tasks you want to examine.",
      done: false,
      disabled: false,
      disabledText: "Not available in demo",
      url: ROUTES.dam.processPresets,
    },
    {
      id: "TEAMS",
      name: "Set Up Teams",
      description: "Create your teams, assign users, and select their tasks.",
      done: false,
      disabled: false,
      disabledText: "Not available in demo",
      url: ROUTES.dam.teamSettings,
    },
    {
      id: "WORKSHOP",
      name: "Inform Participants",
      description: "Hold the setup workshop and share the participant link.",
      done: false,
      disabled: false,
      disabledText: "Not available in demo",
      url: ROUTES.dam.workshop,
    },
    {
      id: "BLKLST",
      name: "Exclude Apps & Websites",
      description:
        "Set mandatory app and website exclusions before the Scoping phase.",
      done: false,
      disabled: null,
      disabledText: "Not available in demo",
      url: ROUTES.dam.blacklisting,
    },
  ];

  let scopingSteps = [
    {
      id: "START_WHTLST",
      name: "Start Whitelist Data Collection",
      description: "Find out which apps and websites your team uses.",
      done: false,
      disabled: true,
      disabledText: "Available once your project setup has been completed.",
      url: ROUTES.dam.whitelistPhase,
    },
    {
      id: "END_WHTLST",
      name: "End Whitelist Data Collection",
      description:
        "Finish collecting the information needed to choose relevant apps and websites.",
      done: false,
      disabled: true,
      disabledText: "Available once scoping data collection has started.",
      url: ROUTES.dam.endWhitelistPhase,
    },
    {
      id: "DEF_WHTLST",
      name: "Define Whitelist",
      description:
        "Choose which apps and websites to include in further data collection.",
      done: false,
      disabled: true,
      disabledText: "Available once scoping data collection has finished.",
      url: ROUTES.dam.whitelisting,
    },
    {
      id: "APPNAME_MAP",
      supportingLabel: "Optional",
      name: "Name Apps & Websites",
      description:
        "Give apps and websites clear names so your team can recognise them.",
      done: false,
      disabled: true,
      disabledText: "Available once scoping data collection has started.",
      url: ROUTES.dam.appnamemapping,
    },
  ];

  let taskRegistrationSteps = [
    {
      id: "START_TASK_REG",
      name: "Start Task Registration",
      description: "Let your team start registering the tasks they work on.",
      done: false,
      disabled: true,
      disabledText: "Available once Scoping has been completed.",
      url: ROUTES.dam.StartTaskRegistration,
    },
    {
      id: "END_TASK_REG",
      name: "End Task Registration",
      description:
        "Stop task registration when your team has finished collecting task data.",
      done: false,
      disabled: true,
      disabledText: "Available once task registration has started.",
      url: ROUTES.dam.endTaskRegistration,
    },
    {
      id: "CHECK_ANON",
      name: "Review Anonymization",
      description: "Review the anonymized data before moving on to Analysis.",
      done: false,
      disabled: true,
      disabledText: "Available once task registration has started.",
      url: ROUTES.processRegistration.anonymizationCheck,
    },
    {
      id: "NUMBER_ASSIST",
      name: "Define Number Ranges",
      description:
        "Define number ranges for your project, or confirm that none are needed.",
      done: false,
      disabled: true,
      disabledText: "Available once task registration has started.",
      url: ROUTES.dam.numberRangeAssistant,
    },
    {
      id: "REG_OVERVIEW",
      supportingLabel: "Overview",
      name: "Review Team Progress",
      description:
        "See which tasks your team has registered and where information is still missing.",
      done: false,
      disabled: true,
      disabledText: "Available once task registration has started.",
      url: ROUTES.processRegistration.dashboard,
    },
  ];

  let dataCollectionSteps = [
    {
      id: "START_DATA_COLL",
      name: "Start Data Collection",
      description: "Start recording how your team works in the selected apps and websites.",
      done: false,
      disabled: true,
      disabledText: "Not available in demo",
      url: ROUTES.dam.startDataCollection, // data collection
    },
    {
      id: "END_DATA_COLL",
      name: "End Data Collection",
      description: "Stop recording when enough activity data has been collected.",
      done: false,
      disabled: true,
      disabledText:
        "Available once the end-phase backend endpoint is connected",
      // No route yet: there is no end-phase endpoint to link to. CardColumn
      // renders no link for a falsy href, so the card stays inert even for the
      // admins who bypass `disabled` via isStepDisabledForUser.
      url: null,
    },
    {
      id: "PREPROCESS_DATA",
      name: "Preprocess Data",
      description: "Clean and prepare the collected data for Analysis.",
      done: false,
      disabled: true,
      disabledText: "Not available in demo",
      url: ROUTES.preprocessing.appPreprocessing, // {`${ROUTES.preprocessing.appPreprocessing}?analysisid=${analysisid}&idproject=${idproject}`}
    },
  ];

  let analysisSteps = [
    {
      id: "START_ANALYSIS",
      name: "Analysis",
      description: "Analyze collected data",
      done: false,
      disabled: true,
      disabledText: "Complete all Discovery steps first",
      url: ROUTES.dam.analysis.workflow,
    },
  ];

  const analysisViews = [
    {
      name: "Usage Analysis",
      description:
        "See which apps and websites your team uses and how time is spent.",
      url: ROUTES.dam.analysis.treemap,
    },
    {
      name: "Workflow Analysis",
      description:
        "Explore how tasks are carried out and how work flows between applications.",
      url: ROUTES.dam.analysis.workflow,
    },
  ];

  let phases = [
    { label: "Setup", steps: setupSteps },
    { label: "Scoping", steps: scopingSteps },
    {
      label: "Discovery",
      steps: taskRegistrationSteps,
    },
    {
      label: "Discovery Extended",
      steps: dataCollectionSteps,
    },
    {
      label: "Analysis",
      steps: analysisSteps,
    },
  ];
  $: ({ currentProjectProgress } = $projectListStore);
  let currentTime = Date.now();
  // Keep this before visiblePhases: phases is reassigned inside
  // updateStepsFromResponse, which Svelte cannot see when ordering reactive
  // statements. Declared later, visiblePhases/highlightedStep stay stale and a
  // completed step can remain highlighted as the current step.
  $: currentTime,
    $uiFeatureStore.projectProgress.blacklisting,
    $uiFeatureStore.projectProgress.checkAnonymization,
    $uiFeatureStore.projectProgress.numberAssistant,
    updateStepsFromResponse(currentProjectProgress);
  $: visiblePhases = phases
    .filter(
      (phase) =>
        phase.label !== "Discovery Extended" ||
        $uiFeatureStore.projectProgress.discoveryExtended,
    )
    .map((phase) => ({
      ...phase,
      steps: phase.steps.filter(
        (step) =>
          (step.id !== "BLKLST" ||
            $uiFeatureStore.projectProgress.blacklisting) &&
          (step.id !== "CHECK_ANON" ||
            $uiFeatureStore.projectProgress.checkAnonymization) &&
          (step.id !== "NUMBER_ASSIST" ||
            $uiFeatureStore.projectProgress.numberAssistant) &&
          (step.id !== "PREPROCESS_DATA" ||
            $uiFeatureStore.projectProgress.preprocessData),
      ),
    }));

  const { getProjectProgress } = projectListStore;
  const navigate = useNavigate();
  const urlParams = useUrlParams();
  let redirected = false;
  let sideDataProjectId = null;
  let dataHealthDetailsProjectId = null;
  let loading = true;
  let loadingProjectId = null;
  let showProgressGuide = false;
  let progressGuideStep = 0;
  let progressGuideSteps = [];
  let expandedPhases = { Discovery: true };
  let expandedProjectId = null;

  function startProgressGuide() {
    if (loading) return;
    const phaseIndex = visiblePhases.findIndex(
      (phase, index) =>
        showPhaseCards(visiblePhases, index) &&
        (phase.label === "Analysis" || !isPhaseComplete(phase)),
    );
    if (phaseIndex < 0) return;
    const phase = visiblePhases[phaseIndex];
    const steps = phase.steps.filter((step) => isPhaseStepVisible(phase, step));
    const findStep = (id) => steps.find((step) => step.id === id);
    const phaseHeading = (label) =>
      document
        .querySelector(`[data-guide-phase-heading="${label}"]`)
        ?.closest(".step")
        ?.querySelector(".step-header");
    const cardTarget = (id) => `[data-guide-step="${id}"]`;
    const canOpen = (step) => step && step.url && !isStepDisabledForUser(step);
    const guideSteps = [];
    let statusText = "";
    let metricsText =
      "See how many people and teams are taking part and when each phase was completed.";

    function addCard(step, text) {
      if (!step) return;
      guideSteps.push({
        target: cardTarget(step.id),
        highlightSelector: ".info_card",
        title: step.name,
        text: text ?? getStepDescription(step),
      });
    }

    function addCards(cards, title, text) {
      if (!cards.length) return;
      guideSteps.push({
        target: `[data-guide-phase="${phase.label}"]`,
        highlightSelector: cards
          .map((step) => `${cardTarget(step.id)} .info_card`)
          .join(", "),
        title,
        text,
      });
    }

    if (phase.label === "Setup") {
      statusText =
        "Your project is in Setup. Complete the required tasks in order to unlock Scoping.";
      const nextStep = steps.find(
        (step) => !step.done && !step.supportingLabel,
      );
      if (nextStep) {
        addCard(
          nextStep,
          canOpen(nextStep)
            ? `${nextStep.description} This is your next setup task. Open this card after the tour to continue.`
            : getStepDescription(nextStep),
        );
        const remaining = steps.filter(
          (step) => !step.done && step !== nextStep,
        );
        addCards(
          remaining,
          "What follows in Setup",
          remaining
            .map((step) => `${step.name}: ${getStepDescription(step)}`)
            .join(" "),
        );
      }
    } else if (phase.label === "Scoping") {
      const start = findStep("START_WHTLST");
      const end = findStep("END_WHTLST");
      if (!start?.done) {
        statusText =
          "Your project is ready for Scoping. Collect information about the apps and websites your team uses.";
        addCard(start);
      } else if (!end?.done) {
        statusText =
          "Your project is in Scoping. App and website data collection is running.";
        addCard(
          end,
          isWhitelistMinimumReached()
            ? "The recommended collection duration has been reached. Open this card after the tour when you are ready to finish scoping data collection."
            : `${getStepDescription(end)} We recommend waiting ${MIN_WHITELIST_DURATION_DAYS} days so apps used only once a week are included.`,
        );
        const naming = findStep("APPNAME_MAP");
        if (canOpen(naming)) addCard(naming, `Optional: ${naming.description}`);
      } else {
        statusText =
          "Scoping data collection has finished. Choose the apps and websites to include before moving on to Discovery.";
        addCard(findStep("DEF_WHTLST"));
        const naming = findStep("APPNAME_MAP");
        if (canOpen(naming)) addCard(naming, `Optional: ${naming.description}`);
      }
    } else if (phase.label === "Discovery") {
      const start = findStep("START_TASK_REG");
      const end = findStep("END_TASK_REG");
      const reviews = steps.filter(
        (step) =>
          ["CHECK_ANON", "NUMBER_ASSIST"].includes(step.id) && !step.done,
      );
      metricsText =
        "Check how many tasks your team has registered and when the latest registration took place. The milestones show which phases are already complete.";
      if (!start?.done) {
        statusText =
          "Your project is ready for Discovery. Start task registration so your team can record the tasks they work on.";
        addCard(start);
      } else {
        statusText = end?.done
          ? "Task registration has finished. Review the results and complete any remaining required tasks to unlock Analysis."
          : "Your project is in Discovery. Task registration is running; you can review team progress and prepare the data in parallel.";
        if (!end?.done) addCard(end);
        const overview = findStep("REG_OVERVIEW");
        if (canOpen(overview)) addCard(overview);
        addCards(
          reviews,
          end?.done ? "Prepare for Analysis" : "Prepare data in parallel",
          reviews
            .map((step) => `${step.name}: ${getStepDescription(step)}`)
            .join(" ") +
            (end?.done
              ? " Complete these required tasks before opening Analysis."
              : " You can complete these tasks while registration is still running; registration must also be finished before Analysis becomes available."),
        );
      }
    } else if (phase.label === "Discovery Extended") {
      statusText =
        "Your project is in Discovery Extended. This phase provides additional activity data collection.";
      const nextStep = steps.find(
        (step) => !step.done && !step.supportingLabel,
      );
      if (nextStep)
        addCard(
          nextStep,
          canOpen(nextStep)
            ? nextStep.description
            : `${getStepDescription(nextStep)} This card is currently unavailable.`,
        );
      const otherSteps = steps.filter(
        (step) => step !== nextStep && !step.done && canOpen(step),
      );
      addCards(
        otherSteps,
        "Further data preparation",
        otherSteps.map((step) => `${step.name}: ${step.description}`).join(" "),
      );
    } else if (phase.label === "Analysis") {
      const available = !phase.steps[0].disabled;
      statusText = available
        ? "Your project is ready for Analysis. Use these two views to explore the collected data."
        : `Analysis is currently unavailable. ${phase.steps[0].disabledText}`;
      if (available) {
        for (const view of analysisViews) {
          guideSteps.push({
            target: `[data-guide-analysis="${view.name}"]`,
            highlightSelector: ".info_card",
            title: view.name,
            text: view.description,
          });
        }
      }
      metricsText =
        "Use the project milestones and registration totals to put your analysis results in context.";
    }

    const completedPhases = visiblePhases
      .slice(0, phaseIndex)
      .filter((candidate) => isPhaseComplete(candidate));
    guideSteps.unshift({
      target: phaseHeading(phase.label),
      title: "See where your project stands",
      text:
        statusText +
        (completedPhases.length
          ? " Earlier phases are complete. After the tour, use Completed to show or hide their cards."
          : " The count beside each phase shows how many required tasks are complete."),
    });
    guideSteps.push({
      target: '[data-guide="project-metrics"]',
      title: "Check project progress",
      text: metricsText,
    });
    const nextPhase = visiblePhases[phaseIndex + 1];
    if (nextPhase) {
      guideSteps.push({
        target: phaseHeading(nextPhase.label),
        title: `Next: ${nextPhase.label}`,
        text:
          nextPhase.label === "Analysis"
            ? "Analysis requires all enabled, required Discovery tasks to be completed. Usage Analysis shows application usage; Workflow Analysis shows how tasks are carried out."
            : `Complete the required tasks in ${phase.label} to reveal the ${nextPhase.label} cards. Optional and overview cards do not count towards phase completion.`,
      });
    }
    progressGuideSteps = guideSteps.filter(({ target }) => {
      const element =
        typeof target === "string" ? document.querySelector(target) : target;
      return (
        element &&
        getComputedStyle(element).visibility === "visible" &&
        element.getBoundingClientRect().width > 0 &&
        element.getBoundingClientRect().height > 0
      );
    });
    progressGuideStep = 0;
    showProgressGuide = progressGuideSteps.length > 0;
  }

  $: ({ idproject } = $urlParams);
  $: if (expandedProjectId !== idproject) {
    expandedPhases = {
      Discovery: true,
      Setup: ["#setup-workshop", "#setup-exclusions"].includes(
        window.location.hash,
      ),
    };
    expandedPhases.Scoping = ["#scoping-start", "#scoping-end"].includes(
      window.location.hash,
    );
    showProgressGuide = false;
    expandedProjectId = idproject;
  }
  $: hasRequiredParams = Boolean(idproject);

  $: if (!hasRequiredParams && !redirected) {
    redirected = true;
    Promise.resolve(navigate(ROUTES.dam.root, { replace: true })).catch(() => {
      window.location.replace(ROUTES.dam.root);
    });
  }

  async function loadProjectData(projectId) {
    if (!projectId || loadingProjectId === projectId) return;
    loadingProjectId = projectId;
    loading = true;
    try {
      await Promise.all([
        getProjectProgress(projectId, { force: true }),
        loadProjectSideData(projectId),
      ]);
    } finally {
      if (loadingProjectId === projectId) {
        loadingProjectId = null;
        loading = false;
        if (
          [
            "#setup-workshop",
            "#setup-exclusions",
            "#scoping-start",
            "#scoping-end",
          ].includes(window.location.hash)
        ) {
          await tick();
          document
            .getElementById(window.location.hash.slice(1))
            ?.scrollIntoView({ block: "center" });
        }
      }
    }
  }

  $: if (hasRequiredParams) {
    loadProjectData(idproject);
  }
  $: isAdmin = $role?.role === "admin";

  function updateProjectInfoValue(id, value) {
    projectInfo = projectInfo.map((info) =>
      info.id === id ? { ...info, value: String(value ?? "-") } : info,
    );
  }

  function updateDataHealthValue(id, value, statusText = null) {
    dataHealthInfo = dataHealthInfo.map((info) =>
      info.id === id
        ? { ...info, value, ...(statusText ? { statusText } : {}) }
        : info,
    );
  }

  function updateDataHealthDetails(details) {
    // Details are keyed by backend id so removed/disabled checks can simply be
    // omitted by the API without breaking the UI.
    dataHealthDetailsInfo = dataHealthDetailsInfo.map((info) => {
      const detail = details?.[info.id];
      if (!detail) return info;
      return {
        ...info,
        status: detail.status ?? null,
        statusText: detail.statusText || info.statusText,
      };
    });
  }

  function resetDataHealthDetails() {
    // Clear old project results before a manual re-check starts.
    dataHealthDetailsInfo = dataHealthDetailsInfo.map((info) => ({
      ...info,
      status: null,
    }));
  }

  function getHealthStatusClass(value) {
    if (value === null || value === undefined) return "unknown";
    if (value === "yellow") return "yellow";
    if (value === "red") return "red";
    if (value === "green") return "green";
    return value ? "green" : "red";
  }

  function getHealthStatusTitle(info) {
    if (info.statusText) return info.statusText;
    if (info.value === null || info.value === undefined)
      return "Status unknown.";
    return info.value ? "Data is available." : "Required data is missing.";
  }

  function getHealthStatusRule(info) {
    return dataHealthStatusRules[info.id] || "";
  }

  const isStepInProgress = (step) =>
    !step.done &&
    ((!step.disabled &&
      (step.inProgress ||
        step.id === "START_ANALYSIS" ||
        step.id === "END_TASK_REG")) ||
      (step.id === "END_WHTLST" &&
        !isWhitelistMinimumReached() &&
        isWhitelistRunning()));

  $: highlightedStep = visiblePhases
    .flatMap((phase, phaseIndex) =>
      showPhaseCards(visiblePhases, phaseIndex)
        ? phase.steps.filter(
            (step) =>
              isPhaseStepVisible(phase, step) &&
              !step.supportingLabel &&
              !["CHECK_ANON", "NUMBER_ASSIST"].includes(step.id) &&
              !step.done &&
              (!step.disabled ||
                (step.id === "END_WHTLST" &&
                  phase.steps.some(
                    (candidate) =>
                      candidate.id === "START_WHTLST" && candidate.done,
                  ))),
          )
        : [],
    )
    .slice(-1)[0];

  $: taskRegistrationFinished = taskRegistrationSteps.some(
    (step) => step.id === "END_TASK_REG" && step.done,
  );
  $: isStepVisuallyInProgress = (step) =>
    step === highlightedStep ||
    (taskRegistrationFinished &&
      ["CHECK_ANON", "NUMBER_ASSIST"].includes(step.id) &&
      !step.done &&
      !step.disabled);

  const isStepDisabledForDisplay = (step) => step.disabled;
  const isStepDisabledForUser = (step) => step.disabled && !isAdmin;

  function getPhaseBoundarySteps(phase) {
    if (phase.label === "Scoping") {
      return { start: "START_WHTLST", end: "END_WHTLST" };
    }
    if (phase.label === "Discovery") {
      return { start: "START_TASK_REG", end: "END_TASK_REG" };
    }
    return null;
  }

  function getPhaseTotalSteps(phase) {
    return phase.steps.filter((step) => !step.supportingLabel).length;
  }

  function getPhaseCompletedSteps(phase) {
    return phase.steps.filter((step) => !step.supportingLabel && step.done)
      .length;
  }

  function isPhaseComplete(phase) {
    return (
      getPhaseTotalSteps(phase) > 0 &&
      getPhaseCompletedSteps(phase) === getPhaseTotalSteps(phase)
    );
  }

  function showPhaseCards(visiblePhases, phaseIndex) {
    if (isAdmin) return true;
    const isComplete = (phase) =>
      getPhaseCompletedSteps(phase) === getPhaseTotalSteps(phase);

    return (
      isComplete(visiblePhases[phaseIndex]) ||
      visiblePhases.slice(0, phaseIndex).every(isComplete)
    );
  }

  function isPhaseStepVisible(phase, step) {
    if (isAdmin || phase.label === "Scoping" || phase.label === "Discovery")
      return true;
    const boundary = getPhaseBoundarySteps(phase);
    if (!boundary) return true;

    const startStep = phase.steps.find(
      (candidate) => candidate.id === boundary.start,
    );
    if (step.id === boundary.end) return startStep?.done === true;

    return true;
  }

  function getWhitelistEndDate() {
    const startStep = scopingSteps.find((step) => step.id === "START_WHTLST");
    if (!startStep?.createdWhen) return null;

    const startedAt = new Date(startStep.createdWhen);
    if (Number.isNaN(startedAt.getTime())) return null;
    startedAt.setHours(0, 0, 0, 0);
    startedAt.setDate(startedAt.getDate() + MIN_WHITELIST_DURATION_DAYS);
    return startedAt;
  }

  function getWhitelistAvailableAt() {
    const endDate = getWhitelistEndDate();
    return endDate ? formatProjectDate(endDate) : null;
  }

  function isWhitelistMinimumReached() {
    const endDate = getWhitelistEndDate();
    return endDate !== null && currentTime >= endDate.getTime();
  }

  const isWhitelistRunning = () =>
    scopingSteps.some(
      (candidate) => candidate.id === "START_WHTLST" && candidate.done,
    );

  function getSetupPrerequisiteText(step) {
    const hints = {
      TEAMS: "Available once your project scope has been defined.",
      WORKSHOP: "Available once your teams have been set up.",
      BLKLST: "Available once participants have been informed.",
    };
    return hints[step.id] || step.description;
  }

  function getStepDescription(step) {
    if (step.id === "END_WHTLST" && !step.done && isWhitelistRunning()) {
      if (isWhitelistMinimumReached()) {
        return "Recommended duration reached.\nYou can finish scoping data collection now.";
      }
      const availableAt = getWhitelistAvailableAt();
      if (availableAt) {
        return `At least ${MIN_WHITELIST_DURATION_DAYS} days recommended.\nRecommended end: ${availableAt}`;
      }
    }

    return isStepDisabledForDisplay(step)
      ? step.done
        ? step.disabledDoneText || step.disabledText
        : step.disabledText
      : step.description;
  }

  function formatProjectDate(value) {
    const date = new Date(value);
    if (!value || isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  }

  function getPhaseCompletionDate(res, stage, requiredSteps) {
    const recordedSteps =
      res?.project_stages?.find((entry) => entry.stage === stage)
        ?.project_steps || [];
    if (!requiredSteps.length) return "-";
    const timestamps = requiredSteps.map((step) => {
      const recorded = recordedSteps.find((entry) => entry.id === step.id);
      return recorded?.completed && recorded.created_when
        ? new Date(recorded.created_when).getTime()
        : NaN;
    });
    return timestamps.every(Number.isFinite)
      ? formatProjectDate(new Date(Math.max(...timestamps)))
      : "-";
  }

  async function loadProjectSideData(projectId) {
    if (!projectId || sideDataProjectId === projectId) return;
    sideDataProjectId = projectId;
    await Promise.all([
      loadRegistrationMetrics(projectId),
      loadProjectTeamMetrics(projectId),
      loadDataHealthMetrics(projectId),
    ]);
  }

  async function loadRegistrationMetrics(projectId) {
    try {
      const response = await fetchWithSession(
        `/api/damprojects/recordings?idproject=${projectId}`,
      );
      const recordings = await response.json();
      const uniqueRecordings = [
        ...new Map(
          (Array.isArray(recordings) ? recordings : [])
            .map((recording) => ({
              ...recording,
              recorderid: recording?.recorderid ?? recording?.recording_id,
              endtimeutc: recording?.endtimeutc ?? recording?.end_time,
            }))
            .filter((recording) => recording.recorderid && !recording.deleted)
            .map((recording) => [recording.recorderid, recording]),
        ).values(),
      ];
      updateProjectInfoValue("total_registrations", uniqueRecordings.length);
      const newestRegistration = uniqueRecordings.reduce(
        (newest, recording) => {
          const date = new Date(recording.endtimeutc);
          if (isNaN(date.getTime())) return newest;
          return !newest || date > newest ? date : newest;
        },
        null,
      );
      updateProjectInfoValue(
        "last_registration",
        newestRegistration ? formatProjectDate(newestRegistration) : "-",
      );
    } catch (error) {
      console.error("Error fetching project registration metrics:", error);
      updateProjectInfoValue("total_registrations", "-");
      updateProjectInfoValue("last_registration", "-");
    }
  }

  async function loadProjectTeamMetrics(projectId) {
    try {
      const response = await fetchWithSession(
        `/api/damprojects/dam-teams?idproject=${projectId}`,
      );
      const teams = await response.json();
      const assignedUserIds = new Set();
      (Array.isArray(teams) ? teams : []).forEach((team) => {
        (team.damUsers || []).forEach((user) => {
          assignedUserIds.add(user?.id || user);
        });
      });
      updateProjectInfoValue("total_users", assignedUserIds.size);
      updateProjectInfoValue(
        "total_teams",
        (Array.isArray(teams) ? teams : []).length,
      );
    } catch (error) {
      console.error("Error fetching project team metrics:", error);
      updateProjectInfoValue("total_users", "-");
      updateProjectInfoValue("total_teams", "-");
    }
  }

  async function loadDataHealthMetrics(projectId) {
    // Fast automatic smoke check: this uses scope=quick and should stay limited
    // to stream presence/setup status. Recency and per-user detail checks are
    // intentionally loaded separately by loadDataHealthDetailsMetrics().
    try {
      const response = await fetchWithSession(
        `/api/damprojects/DAMProject/${projectId}/data-health?scope=quick`,
      );
      const dataHealth = await response.json();
      const details = dataHealth?.details || {};
      dataHealthInfo = dataHealthInfo.map((info) => {
        const detail = details[info.id];
        if (!detail) return info;
        return {
          ...info,
          value: detail.status ?? null,
          statusText: detail.statusText || info.statusText,
        };
      });
    } catch (error) {
      console.error("Error fetching project data health metrics:", error);
      updateDataHealthValue(
        "applications_status",
        null,
        "Application recording data could not be loaded.",
      );
      updateDataHealthValue(
        "mouseclicks_status",
        null,
        "Mouse click recording data could not be loaded.",
      );
      updateDataHealthValue(
        "keystrokes_status",
        null,
        "Keyboard recording data could not be loaded.",
      );
      updateDataHealthValue(
        "ui_elements_status",
        null,
        "UI element recording data could not be loaded.",
      );
    }
  }

  async function loadDataHealthDetailsMetrics(projectId, force = false) {
    // Manual-only: this request reads recording-health signals and should not
    // run automatically when the Progress view opens.
    if (!projectId || (!force && dataHealthDetailsProjectId === projectId))
      return;
    dataHealthDetailsProjectId = projectId;
    dataHealthDetailsLoading = true;
    dataHealthDetailsError = false;
    dataHealthDetailsLoaded = false;
    resetDataHealthDetails();
    try {
      const response = await fetchWithSession(
        `/api/damprojects/DAMProject/${projectId}/data-health`,
      );
      const dataHealth = await response.json();
      updateDataHealthDetails(dataHealth?.details);
      dataHealthDetailsLoaded = true;
    } catch (error) {
      console.error("Error fetching project data health details:", error);
      dataHealthDetailsError = true;
      updateDataHealthDetails(
        Object.fromEntries(
          dataHealthDetailsInfo.map((info) => [
            info.id,
            {
              status: null,
              statusText: "Data health details could not be loaded.",
            },
          ]),
        ),
      );
    } finally {
      dataHealthDetailsLoading = false;
    }
  }

  function checkDataHealthDetails() {
    // Hide the button immediately by switching into loading state.
    if (dataHealthDetailsLoading) return;
    loadDataHealthDetailsMetrics(idproject, true);
  }

  function getProgressMetric(res, ...keys) {
    const dataHealth = res?.data_health || res?.dataHealth || {};
    for (const key of keys) {
      if (res?.[key] !== undefined && res?.[key] !== null) return res[key];
      if (dataHealth?.[key] !== undefined && dataHealth?.[key] !== null) {
        return dataHealth[key];
      }
    }
    return null;
  }

  function toHealthStatus(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value > 0;
    if (typeof value === "string") {
      if (!value.trim()) return null;
      if (
        ["true", "ok", "healthy", "available"].includes(value.toLowerCase())
      ) {
        return true;
      }
      if (
        ["false", "error", "unhealthy", "missing"].includes(value.toLowerCase())
      ) {
        return false;
      }
      return Number(value) > 0;
    }
    return Boolean(value);
  }

  function updateDataHealthFromProgressResponse(res) {
    const applicationsStatus = toHealthStatus(
      getProgressMetric(
        res,
        "applications_status",
        "applications",
        "application_count",
        "total_applications",
      ),
    );
    const mouseclicksStatus = toHealthStatus(
      getProgressMetric(res, "mouseclicks_status", "mouseclicks"),
    );
    const keystrokesStatus = toHealthStatus(
      getProgressMetric(res, "keystrokes_status", "keystrokes"),
    );
    if (applicationsStatus !== null) {
      updateDataHealthValue(
        "applications_status",
        applicationsStatus,
        applicationsStatus
          ? "Application recording data is available."
          : "No application recording data found.",
      );
    }
    if (mouseclicksStatus !== null) {
      updateDataHealthValue(
        "mouseclicks_status",
        mouseclicksStatus,
        mouseclicksStatus
          ? "Mouse click recording data is available."
          : "No mouse click recording data found.",
      );
    }
    if (keystrokesStatus !== null) {
      updateDataHealthValue(
        "keystrokes_status",
        keystrokesStatus,
        keystrokesStatus
          ? "Keyboard recording data is available."
          : "No keyboard recording data found.",
      );
    }
  }

  function updateStepsFromResponse(res) {
    resetProgressSteps();
    projectInfo = projectInfo.map((info) =>
      ["setup_completed", "scoping_completed", "discovery_completed"].includes(
        info.id,
      )
        ? { ...info, value: "-" }
        : info,
    );
    if (!res || !res.project_stages) {
      return;
    }
    const stageMap = {
      SETUP: setupSteps,
      SCOPING: scopingSteps,
      TASK_REGISTRATION: taskRegistrationSteps,
      DATA_COLLECTION: dataCollectionSteps,
      ANALYSIS: analysisSteps,
    };

    for (const stage of res.project_stages) {
      const steps = stageMap[stage.stage];

      for (const apiStep of stage.project_steps) {
        const step =
          steps?.find((s) => s.id === apiStep.id) ||
          analysisSteps.find((s) => s.id === apiStep.id);
        if (step) {
          step.done = apiStep.completed;
          step.inProgress =
            apiStep.in_progress === true ||
            apiStep.inProgress === true ||
            apiStep.status === "in_progress";
          step.createdWhen = apiStep.created_when;
          // A project step is created when it is marked as completed. Keep the
          // timestamp separately from phase-start metadata so it can be shown
          // next to the Done status on the progress card.
          step.completedWhen = apiStep.created_when;
          step.disabled = !apiStep.enabled;
          if (
            !setupSteps.includes(step) &&
            !scopingSteps.includes(step) &&
            !taskRegistrationSteps.includes(step)
          ) {
            step.description = apiStep.description;
            step.name =
              step.id === "START_ANALYSIS" ? "Analysis" : apiStep.name;
          }
        }
      }
    }

    // A newly created project has no completed steps yet. Keep the initial
    // state local to the UI while the backend has not established a phase.
    const allSteps = [
      ...setupSteps,
      ...scopingSteps,
      ...taskRegistrationSteps,
      ...dataCollectionSteps,
      ...analysisSteps,
    ];
    const isNewProject = allSteps.every((step) => !step.done);
    if (isNewProject) {
      for (const step of setupSteps) {
        step.disabled = false;
        step.inProgress = false;
      }
      for (const step of [
        ...scopingSteps,
        ...taskRegistrationSteps,
        ...dataCollectionSteps,
        ...analysisSteps,
      ]) {
        step.disabled = true;
        step.inProgress = false;
      }
    }

    const requiredSetupSteps = setupSteps.filter(
      (step) =>
        step.id !== "BLKLST" || $uiFeatureStore.projectProgress.blacklisting,
    );
    const setupCompleted = requiredSetupSteps.every(
      (step) => step.done === true,
    );
    const setupPrerequisite = requiredSetupSteps.find((step) => !step.done);
    const startWhitelistStep = scopingSteps.find(
      (step) => step.id === "START_WHTLST",
    );
    if (startWhitelistStep) {
      startWhitelistStep.disabled = !setupCompleted;
      startWhitelistStep.disabledText = setupCompleted
        ? "Find out which apps and websites your team uses."
        : {
            CONFIGURE: "Available once your project scope has been defined.",
            TEAMS: "Available once your teams have been set up.",
            WORKSHOP: "Available once participants have been informed.",
            BLKLST:
              "Available once your initial app and website exclusions have been defined.",
          }[setupPrerequisite.id];
    }

    // Once scoping has started, the complete setup stage is locked.
    const setupLocked = startWhitelistStep?.done === true;
    for (const [index, setupStep] of requiredSetupSteps.entries()) {
      const prerequisite = requiredSetupSteps
        .slice(0, index)
        .find((step) => !step.done);
      setupStep.disabled =
        setupLocked || (!setupStep.done && Boolean(prerequisite));
      if (setupLocked) {
        setupStep.disabledDoneText =
          "Locked because the Scoping phase has started";
        setupStep.disabledText = setupStep.disabledDoneText;
      } else {
        setupStep.disabledDoneText = null;
        setupStep.disabledText = prerequisite
          ? getSetupPrerequisiteText(setupStep)
          : setupStep.description;
      }
    }

    const appnameMappingStep = scopingSteps.find(
      (step) => step.id === "APPNAME_MAP",
    );
    if (appnameMappingStep) {
      appnameMappingStep.disabled = !startWhitelistStep?.done;
      appnameMappingStep.disabledText = startWhitelistStep?.done
        ? "Give apps and websites clear names so your team can recognise them."
        : "Available once scoping data collection has started.";
    }

    const endWhitelistStep = scopingSteps.find(
      (step) => step.id === "END_WHTLST",
    );
    if (endWhitelistStep) {
      // The minimum duration is only a recommendation. Ending earlier asks for
      // confirmation on the End Whitelist page instead of locking the card.
      endWhitelistStep.disabled = startWhitelistStep?.done !== true;
      endWhitelistStep.disabledText =
        "Available once scoping data collection has started.";
    }

    const defineWhitelistStep = scopingSteps.find(
      (step) => step.id === "DEF_WHTLST",
    );
    if (defineWhitelistStep) {
      defineWhitelistStep.disabled = !endWhitelistStep?.done;
      defineWhitelistStep.disabledText = !endWhitelistStep?.done
        ? "Available once scoping data collection has finished."
        : "Choose which apps and websites to include in further data collection.";
    }

    if (endWhitelistStep && defineWhitelistStep?.done === true) {
      endWhitelistStep.done = true;
    }

    const requiredScopingSteps = scopingSteps.filter(
      (step) => !step.supportingLabel,
    );
    const scopingCompleted = requiredScopingSteps.every(
      (step) => step.done === true,
    );
    const startTaskRegistrationStep = taskRegistrationSteps.find(
      (step) => step.id === "START_TASK_REG",
    );
    if (startTaskRegistrationStep) {
      startTaskRegistrationStep.disabled = !scopingCompleted;
      startTaskRegistrationStep.disabledText = scopingCompleted
        ? "Let your team start registering the tasks they work on."
        : "Available once Scoping has been completed.";
    }

    if (startTaskRegistrationStep?.done === true) {
      for (const scopingStep of requiredScopingSteps) {
        scopingStep.done = true;
        scopingStep.disabled = true;
        scopingStep.disabledText = "Locked because Discovery has started";
        scopingStep.disabledDoneText = "Locked because Discovery has started";
      }
    }

    const registrationSteps = taskRegistrationSteps.filter(
      (step) =>
        (step.id !== "CHECK_ANON" ||
          $uiFeatureStore.projectProgress.checkAnonymization) &&
        (step.id !== "NUMBER_ASSIST" ||
          $uiFeatureStore.projectProgress.numberAssistant),
    );
    const requiredRegistrationSteps = registrationSteps.filter(
      (step) => !step.supportingLabel,
    );
    const registrationPrerequisiteHints = {
      START_TASK_REG: "Available once task registration has started.",
      CHECK_ANON: "Available once anonymization has been reviewed.",
    };
    for (const taskStep of registrationSteps) {
      if (taskStep.id === "START_TASK_REG" || taskStep.id === "END_TASK_REG")
        continue;
      taskStep.disabled = !startTaskRegistrationStep?.done;
      taskStep.disabledText = startTaskRegistrationStep?.done
        ? taskStep.description
        : registrationPrerequisiteHints.START_TASK_REG;
    }

    const endTaskRegistrationStep = taskRegistrationSteps.find(
      (step) => step.id === "END_TASK_REG",
    );
    if (endTaskRegistrationStep) {
      endTaskRegistrationStep.disabled = !startTaskRegistrationStep?.done;
      endTaskRegistrationStep.disabledText = startTaskRegistrationStep?.done
        ? endTaskRegistrationStep.description
        : registrationPrerequisiteHints.START_TASK_REG;
    }

    const registrationOverviewStep = taskRegistrationSteps.find(
      (step) => step.id === "REG_OVERVIEW",
    );
    if (registrationOverviewStep) {
      registrationOverviewStep.name = endTaskRegistrationStep?.done
        ? "View Registration Results"
        : "Review Team Progress";
      registrationOverviewStep.description = endTaskRegistrationStep?.done
        ? "Explore the tasks and information collected during task registration."
        : "See which tasks your team has registered and where information is still missing.";
      if (!registrationOverviewStep.disabled) {
        registrationOverviewStep.disabledText =
          registrationOverviewStep.description;
      }
    }

    const endDataCollectionStep = dataCollectionSteps.find(
      (step) => step.id === "END_DATA_COLL",
    );
    if (endDataCollectionStep) {
      endDataCollectionStep.disabled = true;
      endDataCollectionStep.disabledText =
        "Available once the end-phase backend endpoint is connected";
    }

    // The main recording takes its scope from Discovery: started before task registration has ended and its
    // anonymization was reviewed, it recorded nothing (E2E 17.10.).
    const startDataCollectionStep = dataCollectionSteps.find(
      (step) => step.id === "START_DATA_COLL",
    );
    const checkAnonymizationStep = taskRegistrationSteps.find(
      (step) => step.id === "CHECK_ANON",
    );
    if (startDataCollectionStep && !startDataCollectionStep.done) {
      const anonymizationReviewed =
        !$uiFeatureStore.projectProgress.checkAnonymization ||
        checkAnonymizationStep?.done === true;
      if (!endTaskRegistrationStep?.done || !anonymizationReviewed) {
        startDataCollectionStep.disabled = true;
        startDataCollectionStep.disabledText = !endTaskRegistrationStep?.done
          ? "Available once task registration has ended."
          : registrationPrerequisiteHints.CHECK_ANON;
      }
    }
    // Preprocessing works on the collected data; before that it showed as "current step" while Setup was still open.
    const preprocessStep = dataCollectionSteps.find(
      (step) => step.id === "PREPROCESS_DATA",
    );
    if (preprocessStep && !preprocessStep.done && !startDataCollectionStep?.done) {
      preprocessStep.disabled = true;
      preprocessStep.disabledText = "Available once data collection has started.";
    }

    const discoveryCompleted = requiredRegistrationSteps.every(
      (step) => step.done === true,
    );
    const analysisStep = analysisSteps.find(
      (step) => step.id === "START_ANALYSIS",
    );
    if (analysisStep) {
      analysisStep.disabled = !discoveryCompleted;
      analysisStep.disabledText = discoveryCompleted
        ? "Analyze collected data"
        : "Complete all Discovery steps first";
    }

    if (analysisStep?.done === true) {
      for (const discoveryStep of requiredRegistrationSteps) {
        discoveryStep.done = true;
        discoveryStep.disabled = true;
        discoveryStep.disabledText = "Locked because Analysis has started";
        discoveryStep.disabledDoneText = "Locked because Analysis has started";
      }
    }

    // Admin shortcut: once every prerequisite is complete, keep only Analysis
    // actionable. This also covers cards from stages that are not part of the
    // normal progression rules above.
    const allPrerequisitesCompleted = res.project_stages.every((stage) =>
      stage.project_steps.every(
        (step) => step.id === "START_ANALYSIS" || step.completed,
      ),
    );
    if (allPrerequisitesCompleted && !analysisStep?.done) {
      for (const step of [
        ...setupSteps,
        ...requiredScopingSteps,
        ...requiredRegistrationSteps,
        ...dataCollectionSteps,
      ]) {
        step.disabled = true;
        step.disabledText = "Locked because the project is ready for Analysis";
        step.disabledDoneText = step.disabledText;
      }
      analysisStep.disabled = false;
      analysisStep.disabledText = "Analyze collected data";
    }

    // Update project info
    const infoMap = {
      days_from_start: res.days_from_start,
      setup_completed: getPhaseCompletionDate(res, "SETUP", requiredSetupSteps),
      scoping_completed: getPhaseCompletionDate(
        res,
        "SCOPING",
        requiredScopingSteps,
      ),
      discovery_completed: getPhaseCompletionDate(
        res,
        "TASK_REGISTRATION",
        requiredRegistrationSteps,
      ),
    };
    for (const [key, value] of Object.entries(infoMap)) {
      const info = projectInfo.find((i) => i.id === key);
      if (info && value !== undefined) {
        info.value = String(value);
      }
    }
    updateDataHealthFromProgressResponse(res);

    // Trigger reactivity
    projectInfo = [...projectInfo];
    setupSteps = [...setupSteps];
    scopingSteps = [...scopingSteps];
    taskRegistrationSteps = [...taskRegistrationSteps];
    dataCollectionSteps = [...dataCollectionSteps];
    analysisSteps = [...analysisSteps];
    phases = [
      { label: "Setup", steps: setupSteps },
      { label: "Scoping", steps: scopingSteps },
      {
        label: "Discovery",
        steps: taskRegistrationSteps,
      },
      {
        label: "Discovery Extended",
        steps: dataCollectionSteps,
      },
      {
        label: "Analysis",
        steps: analysisSteps,
      },
    ];
    console.log("Updated steps from API response", {
      setupSteps,
      scopingSteps,
      taskRegistrationSteps,
      dataCollectionSteps,
      analysisSteps,
    });
  }

  function resetProgressSteps() {
    setupSteps.forEach((step, index) => {
      step.done = false;
      step.inProgress = false;
      step.createdWhen = null;
      step.completedWhen = null;
      step.disabled = index > 0;
      step.disabledDoneText = null;
      step.disabledText =
        index > 0 ? getSetupPrerequisiteText(step) : step.description;
    });
    [
      scopingSteps,
      taskRegistrationSteps,
      dataCollectionSteps,
      analysisSteps,
    ].forEach((steps) => {
      steps.forEach((step) => {
        step.done = false;
        step.inProgress = false;
        step.createdWhen = null;
        step.completedWhen = null;
        step.disabled = true;
      });
    });
  }

  let fullscreen = false;
  let dataHealthDetailsLoading = false;
  let dataHealthDetailsError = false;
  let dataHealthDetailsLoaded = false;
  let disabledTooltip = { visible: false, x: 0, y: 0, text: "" };
  let dataHealthTooltip = {
    visible: false,
    x: 0,
    y: 0,
    text: "",
    rule: "",
    positionAbove: false,
  };

  $: dataHealthDetailsCurrent = dataHealthDetailsProjectId === idproject;
  // Only show detail results for the project that triggered the check. This
  // prevents stale results flashing after a route/project change.
  $: showDataHealthDetails =
    dataHealthDetailsLoaded && dataHealthDetailsCurrent;
  $: showDataHealthDetailsLoading =
    dataHealthDetailsLoading && dataHealthDetailsCurrent;
  $: showDataHealthDetailsError =
    dataHealthDetailsError && dataHealthDetailsCurrent;

  function shouldShowDataHealthTooltipAbove(y) {
    return typeof window !== "undefined" && window.innerHeight - y < 120;
  }

  function toggleFullscreen() {
    fullscreen = !fullscreen;
  }

  function showDisabledTooltip(event, text) {
    disabledTooltip = {
      visible: true,
      x: event.clientX,
      y: event.clientY,
      text,
    };
  }

  function moveDisabledTooltip(event) {
    disabledTooltip = {
      ...disabledTooltip,
      x: event.clientX,
      y: event.clientY,
    };
  }

  function hideDisabledTooltip() {
    disabledTooltip = { ...disabledTooltip, visible: false };
  }

  function showDataHealthTooltip(event, info) {
    // Custom tooltip is used instead of title="" because the KPI dots sit at the
    // right edge of the sidebar; positioning left/above keeps text readable.
    dataHealthTooltip = {
      visible: true,
      x: event.clientX,
      y: event.clientY,
      text: getHealthStatusTitle(info),
      rule: getHealthStatusRule(info),
      positionAbove: shouldShowDataHealthTooltipAbove(event.clientY),
    };
  }

  function moveDataHealthTooltip(event) {
    dataHealthTooltip = {
      ...dataHealthTooltip,
      x: event.clientX,
      y: event.clientY,
      positionAbove: shouldShowDataHealthTooltipAbove(event.clientY),
    };
  }

  function hideDataHealthTooltip() {
    dataHealthTooltip = { ...dataHealthTooltip, visible: false };
  }

  onMount(() => {
    const unregisterTour = registerViewTour(startProgressGuide);
    const clock = window.setInterval(() => {
      currentTime = Date.now();
    }, 60000);

    return () => {
      unregisterTour();
      window.clearInterval(clock);
    };
  });

  $$restProps;
</script>

<Header
  breadcrumb="Project Progress"
  section="Progress"
  tooltipText="Track the current status and progress of this project."
></Header>

<SectionWrapper
  fitContent
  {fullscreen}
  seamless={true}
  seamlessPadding={true}
  {loading}
  loadingTitle="Loading project progress."
  loadingHint="Please wait while the current project status is prepared."
>
  <div slot="main" class="progress-section">
    <ProgressIndicator vertical>
      {#each visiblePhases as phase, phaseIndex}
        <ProgressStep
          label={phase.label}
          description={showPhaseCards(visiblePhases, phaseIndex)
            ? ""
            : `Available once ${visiblePhases[phaseIndex - 1].label} has been completed.`}
          interactive={false}
          completedLabel="Completed"
          collapsible={phase.label !== "Analysis" && isPhaseComplete(phase)}
          expanded={Boolean(expandedPhases[phase.label])}
          on:toggle={() => {
            expandedPhases = {
              ...expandedPhases,
              [phase.label]: !expandedPhases[phase.label],
            };
          }}
          totalSteps={phase.label === "Analysis"
            ? null
            : getPhaseTotalSteps(phase)}
          completedSteps={phase.label === "Analysis"
            ? null
            : getPhaseCompletedSteps(phase)}
        >
          <svelte:fragment slot="icon">
            <span class="phase-number" data-guide-phase-heading={phase.label}>
              {phaseIndex + 1}
            </span>
          </svelte:fragment>
          {#if showPhaseCards(visiblePhases, phaseIndex)}
            <div class="card-container" data-guide-phase={phase.label}>
              {#if phase.label === "Analysis"}
                {#each analysisViews as view}
                  <div
                    class="progress-card-wrapper"
                    data-guide-analysis={view.name}
                    class:disabled-card={phase.steps[0].disabled}
                    class:in-progress-card={!phase.steps[0].disabled}
                  >
                    <CardColumn
                      href={isStepDisabledForUser(phase.steps[0])
                        ? undefined
                        : `${view.url}?idproject=${encodeURIComponent(idproject)}`}
                      clickable={!isStepDisabledForUser(phase.steps[0])}
                      style={phase.steps[0].disabled
                        ? `background: var(--pax-smoke); opacity: 0.5; cursor: ${isStepDisabledForUser(phase.steps[0]) ? "not-allowed" : "pointer"};`
                        : "background: var(--pax-black); color: var(--pax-white); cursor: pointer;"}
                    >
                      <div
                        class="icon-label"
                        class:in-progress-label={!phase.steps[0].disabled}
                      >
                        {#if phase.steps[0].disabled}
                          <CheckXIcon /> <span>Disabled</span>
                        {:else}
                          <span class="status-icon" aria-hidden="true"></span>
                          <span>Available</span>
                        {/if}
                      </div>
                      <p class="step-name">{view.name}</p>
                      <p class="step-description">
                        {phase.steps[0].disabled
                          ? phase.steps[0].disabledText
                          : view.description}
                      </p>
                    </CardColumn>
                  </div>
                {/each}
              {:else}
                {#each phase.steps.filter( (step) => isPhaseStepVisible(phase, step), ) as step}
                  <div
                    class="progress-card-wrapper"
                    data-guide-step={step.id}
                    id={{
                      WORKSHOP: "setup-workshop",
                      BLKLST: "setup-exclusions",
                      START_WHTLST: "scoping-start",
                      END_WHTLST: "scoping-end",
                    }[step.id]}
                    class:in-progress-card={isStepVisuallyInProgress(step)}
                    class:todo-card={["CHECK_ANON", "NUMBER_ASSIST"].includes(
                      step.id,
                    ) &&
                      !step.done &&
                      !step.disabled &&
                      !isStepVisuallyInProgress(step)}
                    class:disabled-card={isStepDisabledForDisplay(step) &&
                      !isStepVisuallyInProgress(step)}
                    class:disabled-done-card={isStepDisabledForDisplay(step) &&
                      step.done &&
                      !step.supportingLabel}
                    on:mouseenter={(event) =>
                      isStepDisabledForDisplay(step) &&
                      showDisabledTooltip(
                        event,
                        step.done
                          ? step.disabledDoneText || step.disabledText
                          : step.disabledText,
                      )}
                    on:mousemove={(event) =>
                      isStepDisabledForDisplay(step) &&
                      moveDisabledTooltip(event)}
                    on:mouseleave={hideDisabledTooltip}
                  >
                    <CardColumn
                      href={isStepDisabledForUser(step) ? undefined : step.url}
                      keepParams={true}
                      style={isStepVisuallyInProgress(step)
                        ? `background: var(--pax-black); color: var(--pax-white); cursor: ${isStepDisabledForUser(step) ? "not-allowed" : "pointer"};`
                        : isStepDisabledForDisplay(step) &&
                            step.done &&
                            !step.supportingLabel
                          ? `background: #8ef09580; cursor: ${isStepDisabledForUser(step) ? "not-allowed" : "pointer"};`
                          : isStepDisabledForDisplay(step)
                            ? `background: #f2f2f2; opacity: 0.5; cursor: ${isStepDisabledForUser(step) ? "not-allowed" : "pointer"};`
                            : step.done && !step.supportingLabel
                              ? "background: #8ef09580;"
                              : "background: #f2f2f2;"}
                    >
                      <div
                        class="icon-label"
                        class:in-progress-label={isStepVisuallyInProgress(step)}
                      >
                        {#if step.supportingLabel}
                          <CheckEmptyIcon /> <span>{step.supportingLabel}</span>
                        {:else if step.done}
                          <CheckIcon />
                          <span
                            >Done{step.completedWhen
                              ? ` · ${formatProjectDate(step.completedWhen)}`
                              : ""}</span
                          >
                        {:else if ["CHECK_ANON", "NUMBER_ASSIST"].includes(step.id) && !step.disabled}
                          <CheckEmptyIcon /> <span>To do</span>
                        {:else if isStepInProgress(step)}
                          <span class="status-icon" aria-hidden="true"></span>
                          <span>In progress</span>
                        {:else if isStepVisuallyInProgress(step)}
                          <span class="status-icon" aria-hidden="true"></span>
                          <span>Current step</span>
                        {:else if isStepDisabledForDisplay(step)}
                          <CheckXIcon /> <span>Disabled</span>
                        {:else}
                          <CheckEmptyIcon /> <span>Available</span>
                        {/if}
                      </div>
                      <p class="step-name">{step.name}</p>
                      <p class="step-description">{getStepDescription(step)}</p>
                    </CardColumn>
                  </div>
                {/each}
              {/if}
            </div>
          {/if}
        </ProgressStep>
      {/each}
    </ProgressIndicator>
  </div>

  <div slot="menu" class="project-info project-info-menu">
    <div data-guide="project-metrics">
      <Accordion
        heading="Info & Milestones"
        open={true}
        seamless={true}
        seamlessPadding={true}
      >
        <div class="project-info project-metrics-content">
          {#each projectInfo as info}
            <SideKPI
              name={info.name}
              value={info.value}
              tooltipText={info.tooltipText}
            />
          {/each}
        </div>
      </Accordion>
    </div>
    {#if isAdmin}
      <Accordion
        heading="Data Health"
        open={false}
        seamless={true}
        seamlessPadding={true}
      >
        <div class="project-info">
          {#each dataHealthInfo as info}
            <SideKPI
              name={info.name}
              value={info.value}
              tooltipText={info.tooltipText}
            >
              <span
                slot="value"
                class={getHealthStatusClass(info.value)}
                on:mouseenter={(event) => showDataHealthTooltip(event, info)}
                on:mousemove={moveDataHealthTooltip}
                on:mouseleave={hideDataHealthTooltip}
              >
                <Elipse />
              </span>
            </SideKPI>
          {/each}
        </div>
        {#if !showDataHealthDetails && !showDataHealthDetailsLoading}
          <div class="data-health-actions">
            <Button

              kind="secondary"
              text="Check Data Health Details"
              disabled={showDataHealthDetailsLoading}
              on:click={checkDataHealthDetails}
            />
          </div>
        {/if}
        {#if showDataHealthDetailsLoading}
          <div class="data-health-loading">
            <Loading small withOverlay={false} />
            <span>Checking Data Health Details...</span>
          </div>
        {:else if showDataHealthDetailsError}
          <p class="data-health-error">
            Data health details could not be loaded.
          </p>
        {/if}
        {#if showDataHealthDetails}
          <div class="project-info data-health-details">
            {#each dataHealthDetailsInfo as info}
              {#if info.status !== null && info.status !== undefined}
                <SideKPI
                  name={info.name}
                  value={info.status}
                  tooltipText={info.tooltipText}
                >
                  <span
                    slot="value"
                    class={getHealthStatusClass(info.status)}
                    on:mouseenter={(event) =>
                      showDataHealthTooltip(event, info)}
                    on:mousemove={moveDataHealthTooltip}
                    on:mouseleave={hideDataHealthTooltip}
                  >
                    <Elipse />
                  </span>
                </SideKPI>
              {/if}
            {/each}
          </div>
        {/if}
      </Accordion>
    {/if}
  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showProgressGuide}
  bind:currentStep={progressGuideStep}
  steps={progressGuideSteps}
  allowInteraction={false}
/>

{#if disabledTooltip.visible}
  <div
    class="disabled-card-tooltip"
    style={`left: ${disabledTooltip.x}px; top: ${disabledTooltip.y}px;`}
  >
    {disabledTooltip.text}
  </div>
{/if}

{#if dataHealthTooltip.visible}
  <div
    class="data-health-tooltip"
    class:above={dataHealthTooltip.positionAbove}
    style={`left: ${dataHealthTooltip.x}px; top: ${dataHealthTooltip.y}px;`}
  >
    <div>{dataHealthTooltip.text}</div>
    {#if dataHealthTooltip.rule}
      <div class="tooltip-rule">{dataHealthTooltip.rule}</div>
    {/if}
  </div>
{/if}

<style>
  .project-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .project-info-menu {
    gap: var(--side-menu-content-gap, 1rem);
  }

  .project-metrics-content {
    padding-bottom: 0.5rem;
  }

  .card-container {
    display: grid;
    /* Mindestens 4 Spalten, auf breiten Bildschirmen kommen weitere hinzu,
       damit die Karten nicht flach auseinandergezogen werden. */
    grid-template-columns: repeat(
      auto-fill,
      minmax(min(17rem, calc((100% - 3 * var(--control-gap)) / 4)), 1fr)
    );
    /* Every row as tall as the tallest one, so all cards of a phase match */
    grid-auto-rows: 1fr;
    align-items: stretch;
    gap: var(--control-gap);
    margin-top: 1rem;
  }

  .card-container :global(.info_card_link) {
    display: flex;
    height: 100%;
  }

  /* Height follows the content so titles can wrap, the grid keeps cards in a
     row equally tall. */
  .card-container :global(.info_card) {
    height: 100%;
    min-height: 8rem;
    min-height: var(--progress-card-height, auto);
    border-radius: 0.5rem;
    box-shadow: none;
    justify-content: flex-start;
    gap: 0.625rem;
    outline: 1px solid transparent;
    transition: outline-color 0.15s ease;
  }

  .card-container :global(.info_card_link:hover .info_card) {
    box-shadow: none !important;
    outline-color: var(--pax-black);
    transform: none !important;
  }

  .todo-card :global(.info_card) {
    outline-color: var(--pax-black);
  }

  .in-progress-card :global(.info_card_link:hover .info_card) {
    outline-color: transparent;
  }

  .in-progress-card::after {
    position: absolute;
    inset: -0.125rem;
    padding: 0.125rem;
    border-radius: 0.625rem;
    background: conic-gradient(
      from var(--border-angle),
      transparent 0deg 120deg,
      var(--pax-black) 150deg 180deg,
      transparent 210deg 300deg,
      var(--pax-black) 330deg 360deg
    );
    content: "";
    pointer-events: none;
    -webkit-mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    mask:
      linear-gradient(#000 0 0) content-box,
      linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: progress-card-border-run 3.6s linear infinite;
  }

  @property --border-angle {
    syntax: "<angle>";
    inherits: false;
    initial-value: 0deg;
  }

  @keyframes progress-card-border-run {
    to {
      --border-angle: 360deg;
    }
  }

  .progress-card-wrapper {
    position: relative;
    height: 100%;
    min-width: 0;
  }

  .disabled-card-tooltip {
    position: fixed;
    z-index: 40000;
    width: max-content;
    max-width: 14rem;
    padding: 0.5rem 0.625rem;
    border-radius: 0.5rem;
    background: var(--pax-black);
    color: var(--pax-white);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.35;
    text-align: left;
    white-space: pre-line;
    pointer-events: none;
    transform: translate(0.75rem, 0.75rem);
  }

  .data-health-tooltip {
    position: fixed;
    z-index: 40000;
    width: max-content;
    max-width: 18rem;
    padding: 0.5rem 0.625rem;
    border-radius: 0.5rem;
    background: var(--pax-black);
    color: var(--pax-white);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.35;
    text-align: left;
    pointer-events: none;
    transform: translate(calc(-100% - 0.75rem), 0.75rem);
  }

  .data-health-tooltip.above {
    transform: translate(calc(-100% - 0.75rem), calc(-100% - 0.75rem));
  }

  .data-health-actions {
    margin: 0.75rem 0;
    width: 100%;
  }

  .data-health-details {
    margin-top: 0.5rem;
  }

  .data-health-loading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
    color: var(--pax-grey-dark);
    font-size: 0.75rem;
  }

  .data-health-error {
    margin: 0 0 0.75rem;
    color: var(--pax-red);
    font-size: 0.75rem;
  }

  .tooltip-rule {
    margin-top: 0.4rem;
    color: #b8bec8;
    font-size: 0.6875rem;
  }

  .step-name {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.25;
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .step-description {
    margin: 0;
    color: #4d5358;
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.35;
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    white-space: pre-line;
  }

  [data-guide-phase="Setup"] :global(.info_card) {
    height: auto;
    min-height: 8rem;
    flex: 1;
  }

  [data-guide-phase="Setup"] .progress-card-wrapper {
    display: flex;
    flex-direction: column;
  }

  [data-guide-phase="Setup"] .step-name {
    white-space: normal;
  }

  [data-guide-phase="Setup"] .step-description {
    display: block;
    overflow: visible;
  }

  :global(.info_card_black) .step-description {
    color: #e5e7eb;
  }

  .progress-section {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  /* "li" raises specificity above ProgressStep's doubled hash in production builds */
  .progress-section :global(li.step.static .step-icon.has-custom-icon) {
    width: 1.75rem;
    height: 1.75rem;
    background: var(--pax-smoke);
    color: var(--pax-black);
  }

  .progress-section :global(li.step .step-header) {
    min-height: 1.75rem;
  }

  .progress-section :global(li.step.vertical::after) {
    left: calc(0.875rem - 0.03125rem);
    width: 0.0625rem;
    top: 2.125rem;
    bottom: 0.375rem;
  }

  .phase-number {
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1;
  }

  .red {
    color: #f08e90;
  }

  .green {
    color: #8ef095;
  }

  .yellow {
    color: #e9f08e;
  }

  .unknown {
    color: #b8bec8;
  }

  .icon-label {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 0.375rem;
    color: #525252;
  }

  .icon-label span {
    font-size: 0.6875rem;
    font-weight: 500;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .status-icon {
    display: inline-block;
    width: 1.125rem;
    height: 1.125rem;
    background-color: currentColor;
    mask: url("/img/assistant/icons/pending.svg") center / contain no-repeat;
    -webkit-mask: url("/img/assistant/icons/pending.svg") center / contain
      no-repeat;
  }

  .in-progress-label {
    color: var(--pax-white);
  }

  .in-progress-card :global(.step-description) {
    color: var(--pax-white);
  }

  :global(.info_card_black) .icon-label {
    color: #f9fbff;
  }
</style>
